//frontend/src/components/layouts/Search.js - Improved version
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, Loader2 } from 'lucide-react';
import { publicInstance } from '../../utils/axiosConfig';
import { debounce } from 'lodash';

const Search = () => {
    const [keyword, setKeyword] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [noResults, setNoResults] = useState(false);
    const navigate = useNavigate();
    const wrapperRef = useRef(null);

    const displayPrice = (product) => {
        if (product.priceType === 'perKg') {
            return `₹${product.price}/kg`;
        } else if (product.priceType === 'perDozen') {
            return `₹${product.dozenPrice}/dozen`;
        } else if (product.weightOptions && product.weightOptions.length > 0) {
            const prices = product.weightOptions.map(opt => opt.price);
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            return minPrice === maxPrice ? 
                `₹${minPrice}` : 
                `₹${minPrice} - ₹${maxPrice}`;
        }
        return '';
    };

    // Reduced debounce time for better UX
    const fetchSuggestions = debounce(async (query) => {
        if (query.length < 2) {
            setSuggestions([]);
            setNoResults(false);
            return;
        }
        
        setIsLoading(true);
        try {
            const { data } = await publicInstance.get(`/products/suggestions?keyword=${query}`);
            
            // Backend should already be filtering for visibility, but we'll double-check
            const visibleSuggestions = data.suggestions.filter(product => product.isVisible !== false);
            
            setSuggestions(visibleSuggestions);
            setNoResults(visibleSuggestions.length === 0);
            
            console.log('Search suggestions:', visibleSuggestions); // Debug log
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setSuggestions([]);
            setNoResults(true);
        } finally {
            setIsLoading(false);
        }
    }, 250); // Reduced from 300ms to 250ms

    useEffect(() => {
        if (keyword) {
            fetchSuggestions(keyword);
        } else {
            setSuggestions([]);
            setNoResults(false);
        }
    }, [keyword]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (keyword.trim()) {
            navigate(`/search/${keyword}`);
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        navigate(`/product/${suggestion._id}`);
        setShowSuggestions(false);
        setKeyword('');
    };

    return (
        <div className="relative w-full" ref={wrapperRef}>
            <form onSubmit={handleSubmit} className="relative">
                <input
                    type="text"
                    placeholder="Search for meat products..."
                    value={keyword}
                    onChange={(e) => {
                        setKeyword(e.target.value);
                        setShowSuggestions(true);
                    }}
                    onFocus={() => {
                        if (keyword.length >= 2) {
                            setShowSuggestions(true);
                        }
                    }}
                    className="w-full pl-4 pr-10 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#dc2626] focus:ring-1 focus:ring-[#dc2626] placeholder-gray-400 text-xs"
                />
                <button
                    type="submit"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#dc2626] hover:text-[#dc2626]/80 transition-colors"
                >
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <SearchIcon className="h-4 w-4" />
                    )}
                </button>
            </form>
            
            {/* Suggestions dropdown with improved visibility */}
            {showSuggestions && keyword.length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-100 max-h-80 overflow-y-auto z-50">
                    {isLoading ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                            <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
                            Searching...
                        </div>
                    ) : noResults ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                            No products found for "{keyword}"
                        </div>
                    ) : suggestions.length > 0 ? (
                        suggestions.map((suggestion) => (
                            <div
                                key={suggestion._id}
                                onClick={() => handleSuggestionClick(suggestion)}
                                className="flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                                <div className="flex-shrink-0 w-12 h-12 mr-3">
                                    {suggestion.images && suggestion.images[0] ? (
                                        <img
                                            src={suggestion.images[0].image}
                                            alt={suggestion.name}
                                            className="w-full h-full object-cover rounded-md"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'https://via.placeholder.com/100?text=No+Image';
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gray-100 rounded-md flex items-center justify-center">
                                            <SearchIcon className="h-5 w-5 text-gray-400" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {suggestion.name}
                                    </p>
                                    <p className="text-xs text-[#dc2626]">
                                        {displayPrice(suggestion)}
                                    </p>
                                </div>
                            </div>
                        ))
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default Search;